import { apiClient } from './client'
import { useAuthStore } from '@/shared/stores/auth'

const BASE = '/api/pexitalk/queue'

export type FormFieldType =
  | 'text' | 'textarea' | 'number' | 'date' | 'time' | 'datetime'
  | 'select' | 'checkbox' | 'phone' | 'email'

/** Champ d'un formulaire réutilisable (RDV). */
export interface FormField {
  id: number
  template: number
  tenant_id: number
  key: string
  label: string
  field_type: FormFieldType
  type_label?: string
  required: boolean
  options: string[]
  help_text: string
  order: number
  created_at: string
  updated_at: string
}

/** Formulaire réutilisable (modèle), p.ex. un formulaire de rendez-vous. */
export interface FormTemplate {
  id: number
  tenant_id: number
  name: string
  description: string
  is_active: boolean
  fields: FormField[]
  field_count?: number
  /** Équipes rattachées à ce formulaire (repli après la campagne). */
  team_ids?: number[]
  /** Agents rattachés à ce formulaire (repli après l'équipe). */
  user_ids?: number[]
  /** Idem mais pour le formulaire de VENTE (kind='sale'). */
  sale_team_ids?: number[]
  sale_user_ids?: number[]
  created_at: string
  updated_at: string
}

export type FormFieldWrite = Partial<
  Pick<FormField, 'template' | 'key' | 'label' | 'field_type' | 'required' | 'options' | 'help_text' | 'order'>
>

/** Statut du cycle de vie d'un RDV. Seul `valide` compte dans les statistiques ;
 *  `confirme` bascule automatiquement à `valide` 48 h après la date du RDV.
 *  `close` / `a_remplacer` remontent du cycle partenaire LabyRDV. */
export type AppointmentStatus =
  | 'en_attente' | 'confirme' | 'valide' | 'close' | 'a_requalifier'
  | 'a_remplacer' | 'non_qualifie' | 'non_valide' | 'annule'

/** Métadonnée d'affichage des statuts (libellé + couleur), ordre du cycle. */
export const APPOINTMENT_STATUSES: Array<{
  value: AppointmentStatus; label: string; color: string
}> = [
  { value: 'en_attente', label: 'En attente', color: '#3b82f6' },     // bleu
  { value: 'confirme', label: 'Confirmé', color: '#4ade80' },         // vert clair
  { value: 'valide', label: 'Validé', color: '#16a34a' },             // vert
  { value: 'close', label: 'Closé', color: '#0f766e' },               // teal foncé
  { value: 'a_requalifier', label: 'À requalifier', color: '#f59e0b' }, // orange
  { value: 'a_remplacer', label: 'À remplacer', color: '#8b5cf6' },   // violet
  { value: 'non_qualifie', label: 'Non qualifié', color: '#9ca3af' }, // gris
  { value: 'non_valide', label: 'Non validé', color: '#9ca3af' },     // gris
  { value: 'annule', label: 'Annulé', color: '#ef4444' },             // rouge
]

export const APPOINTMENT_STATUS_MAP: Record<AppointmentStatus, { label: string; color: string }> =
  Object.fromEntries(APPOINTMENT_STATUSES.map((s) => [s.value, { label: s.label, color: s.color }])) as any

/** Un rendez-vous saisi en fin d'appel. */
export interface Appointment {
  id: number
  tenant_id: number
  campaign: number | null
  campaign_name?: string
  queue_entry: number | null
  contact: number | null
  template: number | null
  template_name?: string
  agent_id: number | null
  status: AppointmentStatus
  status_display?: string
  scheduled_at: string | null
  /** Fuseau d'affichage résolu (campagne › agent › global). */
  timezone?: string
  data: Record<string, any>
  contact_name: string
  phone: string
  /** Liaison LabyRDV : id du RDV côté plateforme partenaires (vide = jamais envoyé). */
  labyrdv_id?: string
  /** Partenaire PexiLine choisi à la prise du RDV (vide = sans partenaire). */
  pexiline_partner_id?: string
  pexiline_partner_name?: string
  labyrdv_sent_at?: string | null
  created_at: string
}

export const FormTemplateAPI = {
  list(opts: { active?: boolean } = {}): Promise<FormTemplate[]> {
    const params = opts.active ? { active: '1' } : undefined
    return apiClient.get(`${BASE}/forms/`, { params }).then((r) => r.data.results || r.data)
  },
  get(id: number): Promise<FormTemplate> {
    return apiClient.get(`${BASE}/forms/${id}/`).then((r) => r.data)
  },
  create(data: { name: string; description?: string }): Promise<FormTemplate> {
    return apiClient.post(`${BASE}/forms/`, data).then((r) => r.data)
  },
  update(id: number, data: Partial<Pick<FormTemplate, 'name' | 'description' | 'is_active'>>): Promise<FormTemplate> {
    return apiClient.patch(`${BASE}/forms/${id}/`, data).then((r) => r.data)
  },
  remove(id: number): Promise<void> {
    return apiClient.delete(`${BASE}/forms/${id}/`)
  },
  /** Définit les équipes / agents rattachés (réconciliation atomique côté
   *  serveur : un seul formulaire par équipe / par agent). */
  setScopes(id: number, scopes: { team_ids: number[]; user_ids: number[]; kind?: 'appointment' | 'sale' }): Promise<FormTemplate> {
    return apiClient.put(`${BASE}/forms/${id}/scopes/`, scopes).then((r) => r.data)
  },
}

export const FormFieldAPI = {
  create(data: FormFieldWrite & { template: number; key: string; label: string }): Promise<FormField> {
    return apiClient.post(`${BASE}/form-fields/`, data).then((r) => r.data)
  },
  update(id: number, data: FormFieldWrite): Promise<FormField> {
    return apiClient.patch(`${BASE}/form-fields/${id}/`, data).then((r) => r.data)
  },
  remove(id: number): Promise<void> {
    return apiClient.delete(`${BASE}/form-fields/${id}/`)
  },
}

/** Champ de formulaire renvoyé dans le détail d'un RDV (libellé + type). */
export interface AppointmentFieldInfo {
  key: string
  label: string
  field_type: FormFieldType
  order: number
  options: string[]
  required: boolean
}

/** Champs éditables d'un RDV (PATCH). `scheduled_at_local` = heure murale
 *  naïve ('2026-06-20T09:00') dans le fuseau du RDV. */
export interface AppointmentUpdate {
  scheduled_at_local?: string | null
  status?: AppointmentStatus
  data?: Record<string, any>
  contact_name?: string
  phone?: string
}

/** Fiche contact structurée jointe au détail d'un RDV. */
export interface AppointmentContactCard {
  name: string
  company: string
  email: string
  city: string
  address: string
  business_sector: string
  phone_operator: string
  last_outcome: string
  notes: string
  call_attempts: number
}

/** Appel + enregistrement reliés au RDV (via le CDR). */
export interface AppointmentCall {
  cdr_id: number
  direction: 'inbound' | 'outbound' | 'internal'
  duration_seconds: number
  started_at: string
  caller_number: string
  called_number: string
  has_recording: boolean
}

/** Détail enrichi d'un RDV (agent, libellés des champs, contact, appel). */
export interface AppointmentDetail extends Appointment {
  agent_name: string
  template_fields: AppointmentFieldInfo[]
  contact_card: AppointmentContactCard | null
  call: AppointmentCall | null
}

export interface AppointmentStats {
  /** Total tous statuts. */
  total: number
  /** RDV « validés » — les seuls qui comptent dans les statistiques. */
  validated: number
  basis: 'created' | 'scheduled'
  by_campaign: Array<{ campaign_id: number; campaign_name: string; count: number; validated: number }>
  by_agent: Array<{ agent_id: number; agent_name: string; count: number; validated: number }>
  by_team: Array<{ team_id: number | null; team_name: string; count: number; validated: number }>
}

export type AppointmentFilters = {
  q?: string; campaign?: number; agent?: number; team?: number
  from?: string; to?: string; basis?: 'created' | 'scheduled'
  /** Filtre par DATE DE CRÉATION du RDV (distinct de la date planifiée). */
  created_from?: string; created_to?: string
  status?: AppointmentStatus
}

export interface LabyrdvPartnersResponse {
  partners: Array<{ id: string; name: string }>
  /** Le compte pexitalk est-il rattaché à un compte PexiLine ? */
  linked: boolean
  configured: boolean
  /** PexiLine n'a pas répondu : la liste est vide faute de mieux, pas parce
   *  que le compte n'a pas de partenaire. */
  error?: boolean
}
// Mémo 60 s, propre à l'utilisateur connecté : un autre compte qui ouvre une
// session sur le même poste ne doit pas hériter de la liste du précédent.
let partnersCache: { at: number; userId: number | null; promise: Promise<LabyrdvPartnersResponse> } | null = null

export const AppointmentAPI = {
  list(params: AppointmentFilters = {}): Promise<Appointment[]> {
    return apiClient.get(`${BASE}/appointments/`, { params }).then((r) => r.data.results || r.data)
  },
  /** Statistiques agrégées (superviseur+) : total + par campagne/agent/équipe. */
  stats(params: AppointmentFilters = {}): Promise<AppointmentStats> {
    return apiClient.get(`${BASE}/appointments/stats/`, { params }).then((r) => r.data)
  },
  get(id: number): Promise<AppointmentDetail> {
    return apiClient.get(`${BASE}/appointments/${id}/`).then((r) => r.data)
  },
  /** Édite un RDV (heure, valeurs du formulaire, contact). Portée par rôle côté
   *  serveur : un agent ne modifie que les siens. Renvoie le détail à jour. */
  update(id: number, payload: AppointmentUpdate): Promise<AppointmentDetail> {
    return apiClient.patch(`${BASE}/appointments/${id}/`, payload).then((r) => r.data)
  },
  /** Export des RDV filtrés. Excel (.xlsx) par défaut, CSV via format='csv'.
   *  Respecte la portée par rôle : un agent n'exporte que les siens.
   *  (Le paramètre s'appelle `fmt` : `format` est réservé par DRF.) */
  exportFile(params: AppointmentFilters = {}, format: 'xlsx' | 'csv' = 'xlsx'): Promise<Blob> {
    return apiClient
      .get(`${BASE}/appointments/export/`, {
        params: { ...params, fmt: format },
        responseType: 'blob',
      })
      .then((r) => r.data)
  },
  /** Partenaires PexiLine rattachés au compte courant (liste « Partenaire »
   *  à la prise d'un RDV). Mémorisé 60 s : consulté à chaque fin d'appel. */
  labyrdvPartners(): Promise<LabyrdvPartnersResponse> {
    const now = Date.now()
    const userId = useAuthStore().user?.id ?? null
    if (partnersCache && partnersCache.userId === userId && now - partnersCache.at < 60_000) {
      return partnersCache.promise
    }
    const promise = apiClient
      .get(`${BASE}/appointments/labyrdv-partners/`)
      .then((r) => r.data as LabyrdvPartnersResponse)
      .catch(() => {
        partnersCache = null
        return { partners: [], linked: false, configured: true, error: true } as LabyrdvPartnersResponse
      })
    partnersCache = { at: now, userId, promise }
    return promise
  },
  /** Envoie (ou ré-envoie) un RDV vers LabyRDV — superviseur+, ou agent
   *  autorisé par un admin (ses RDV). Le serveur exige le statut « Validé »
   *  au premier envoi. */
  sendToLabyRDV(id: number): Promise<{ labyrdv_id: string; sent_at: string }> {
    return apiClient.post(`${BASE}/appointments/${id}/send-to-labyrdv/`).then((r) => r.data)
  },
  /** Envoi en lot vers LabyRDV (max 100) : résultat détaillé par RDV. */
  sendToLabyRDVBulk(ids: number[]): Promise<{
    sent: number
    results: Array<{ id: number; ok: boolean; labyrdv_id?: string; error?: string }>
  }> {
    return apiClient.post(`${BASE}/appointments/send-to-labyrdv/`, { ids }).then((r) => r.data)
  },
}

/** Une VENTE enregistrée (mirror Appointment ; `amount` = montant exploitable). */
export interface Sale {
  id: number
  tenant_id: number
  campaign: number | null
  campaign_name: string
  queue_entry: number | null
  contact: number | null
  template: number | null
  template_name: string
  agent_id: number | null
  amount: string | null
  data: Record<string, any>
  contact_name: string
  phone: string
  created_at: string
}
export interface SaleDetail extends Sale {
  agent_name: string
  template_fields: AppointmentFieldInfo[]
  contact_card: AppointmentContactCard | null
  call: AppointmentCall | null
}
export interface SaleStats {
  total: number
  amount_total: string
  by_campaign: Array<{ campaign_id: number; campaign_name: string; count: number; amount: string }>
  by_agent: Array<{ agent_id: number; agent_name: string; count: number; amount: string }>
  by_team: Array<{ team_id: number | null; team_name: string; count: number }>
}
export type SaleFilters = {
  q?: string; campaign?: number; agent?: number; team?: number; from?: string; to?: string
}
export interface SaleUpdate {
  amount?: string | number | null
  data?: Record<string, any>
  contact_name?: string
  phone?: string
}

export const SaleAPI = {
  list(params: SaleFilters = {}): Promise<Sale[]> {
    return apiClient.get(`${BASE}/sales/`, { params }).then((r) => r.data.results || r.data)
  },
  stats(params: SaleFilters = {}): Promise<SaleStats> {
    return apiClient.get(`${BASE}/sales/stats/`, { params }).then((r) => r.data)
  },
  get(id: number): Promise<SaleDetail> {
    return apiClient.get(`${BASE}/sales/${id}/`).then((r) => r.data)
  },
  update(id: number, payload: SaleUpdate): Promise<SaleDetail> {
    return apiClient.patch(`${BASE}/sales/${id}/`, payload).then((r) => r.data)
  },
}

// ── Suivi d'appels Recouvrement / Fidélisation (admin/owner) ────────────────
export type FollowUpCategory = 'recouvrement' | 'fidelisation'
export type FollowUpStatus = 'a_traiter' | 'valide' | 'rejete'

/** Appel à suivre, créé en fin d'appel par une disposition marquée. */
export interface FollowUpCall {
  id: number
  tenant_id: number
  category: FollowUpCategory
  category_label: string
  campaign: number | null
  campaign_name: string
  queue_entry: number | null
  contact: number | null
  agent_id: number | null
  agent_name: string
  contact_name: string
  phone: string
  agent_notes: string
  status: FollowUpStatus
  status_label: string
  review_note: string
  reviewed_by_id: number | null
  reviewed_by_name: string
  reviewed_at: string | null
  created_at: string
}

export interface FollowUpFilters {
  category?: FollowUpCategory
  status?: FollowUpStatus
  campaign?: number | string
  agent?: number | string
  from?: string
  to?: string
  q?: string
}

export interface FollowUpStats {
  total: number
  by_category: Record<FollowUpCategory, {
    a_traiter: number; valide: number; rejete: number; total: number
  }>
}

export const FollowUpAPI = {
  list(params: FollowUpFilters = {}): Promise<FollowUpCall[]> {
    return apiClient.get(`${BASE}/follow-ups/`, { params }).then((r) => r.data.results || r.data)
  },
  stats(): Promise<FollowUpStats> {
    return apiClient.get(`${BASE}/follow-ups/stats/`).then((r) => r.data)
  },
  review(id: number, payload: { status?: FollowUpStatus; review_note?: string }): Promise<FollowUpCall> {
    return apiClient.patch(`${BASE}/follow-ups/${id}/`, payload).then((r) => r.data)
  },
}

/** Script d'appel réutilisable (équipe, transverse, ou personnel d'agent). */
export interface Script {
  id: number
  tenant_id: number
  team: number | null
  team_name?: string
  name: string
  content: string
  is_active: boolean
  is_personal: boolean
  owner_id: number | null
  assigned_users: number[]
  created_at: string
  updated_at: string
}

export type ScriptWrite = Partial<Pick<Script, 'name' | 'content' | 'is_active' | 'team' | 'assigned_users'>>

export const ScriptAPI = {
  list(opts: { active?: boolean; mine?: boolean } = {}): Promise<Script[]> {
    const params: Record<string, string> = {}
    if (opts.active) params.active = '1'
    if (opts.mine) params.mine = '1'
    return apiClient.get(`${BASE}/scripts/`, { params }).then((r) => r.data.results || r.data)
  },
  create(data: ScriptWrite & { name: string }): Promise<Script> {
    return apiClient.post(`${BASE}/scripts/`, data).then((r) => r.data)
  },
  update(id: number, data: ScriptWrite): Promise<Script> {
    return apiClient.patch(`${BASE}/scripts/${id}/`, data).then((r) => r.data)
  },
  remove(id: number): Promise<void> {
    return apiClient.delete(`${BASE}/scripts/${id}/`)
  },
  /** Crée une réplique personnelle éditable. */
  duplicate(id: number): Promise<Script> {
    return apiClient.post(`${BASE}/scripts/${id}/duplicate/`).then((r) => r.data)
  },
}

/** Un script disponible pour l'agent sur un appel donné. */
export interface CallScript {
  id: number | null
  name: string
  content: string
  source: 'campaign' | 'assigned' | 'personal'
}

/** Bloc-notes personnel de l'agent. */
export const NotebookAPI = {
  get(): Promise<{ content: string; updated_at: string | null }> {
    return apiClient.get(`${BASE}/notebook/`).then((r) => r.data)
  },
  save(content: string): Promise<{ content: string; updated_at: string }> {
    return apiClient.put(`${BASE}/notebook/`, { content }).then((r) => r.data)
  },
}
